import { useState } from "react";
import { Sobreposicao } from "../../components/Sobreposicao.js";
import { Falha, Girando, cx } from "../../components/ui.js";
import {
  ChaveDeVersaoDaFoto,
  ContagemDasFotos,
  FiltrosDasFotos,
  MostrarMaisFotos,
} from "../impressao/FiltrosDasFotos.js";
import { GradeDeFotos } from "../impressao/GradeDeFotos.js";
import { VisorDaFoto } from "../impressao/VisorDaFoto.js";
import {
  LARGURA_DA_ESCOLHA,
  MINIMO_DA_GRADE,
  legendaDaVersao,
  rotuloDeEscolher,
} from "../impressao/ampliar-fotos.js";
import type { FotosPublicadas } from "../impressao/useFotosPublicadas.js";

const LEGENDA_DA_PECA =
  "O retrato da peça publicada, sem a capa da vivência, vai de referência à IA.";
const LEGENDA_DA_FOTO =
  "A imagem que a IA gerou, inteira e sem capa, vai de referência. As fotos sem capa seguem como estão.";

/**
 * A tela de escolha da foto dos Marcadores: tela cheia, com os filtros e a chave de versão no topo
 * e uma grade grande — a foto é o assunto, e escolher a errada gasta uma folha do pacote.
 *
 * Tocar numa foto a escolhe e fecha; a lupa a amplia **sem escolher**, e dentro do visor "Usar esta
 * foto" escolhe, na versão que estiver à vista, e fecha tudo. O Esc fecha só o visor, e depois a escolha.
 *
 * O topo (título, filtros, versão) fica preso enquanto se rola a grade, mas só em telas com altura
 * para isso: num celular deitado ele comeria a tela inteira.
 */
export function EscolherFoto({
  bandeja,
  escolhida,
  aoEscolher,
  aoFechar,
}: {
  bandeja: FotosPublicadas;
  /** A foto que já está escolhida, realçada na grade. */
  escolhida: number | null;
  aoEscolher: (id: number) => void;
  aoFechar: () => void;
}) {
  // A foto aberta no visor. Ela vem das já vistas: um filtro novo não a tira de lá.
  const [ampliando, setAmpliando] = useState<number | null>(null);
  const emVisor = ampliando !== null ? bandeja.vistas.get(ampliando) : undefined;

  const coluna = { maxWidth: LARGURA_DA_ESCOLHA };

  return (
    <Sobreposicao rotulo="Escolher a foto" aoFechar={aoFechar} tamanho="tela">
      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <div className="top-0 z-10 border-b border-borda bg-fundo [@media(min-height:44rem)]:sticky">
          <div className="mx-auto px-4 sm:px-6" style={coluna}>
            <header className="area-segura-superior flex items-center justify-between gap-3 pb-1">
              <h2 className="min-w-0 font-display text-[1.25rem] leading-tight tracking-[-0.01em]">
                Escolher a foto
              </h2>
              <button
                type="button"
                onClick={aoFechar}
                className="-mr-3 inline-flex min-h-11 shrink-0 items-center rounded-peca px-3 text-[0.9375rem] text-texto-suave transition-colors hover:bg-superficie hover:text-texto"
              >
                Fechar
              </button>
            </header>
            <FiltrosDasFotos bandeja={bandeja} contagem={false} className="mb-2" />
            <ChaveDeVersaoDaFoto
              bandeja={bandeja}
              legendaPeca={LEGENDA_DA_PECA}
              legendaFoto={LEGENDA_DA_FOTO}
              comLegenda={false}
            />
          </div>
        </div>

        <div className={cx("area-segura-inferior mx-auto px-4 pt-2 sm:px-6")} style={coluna}>
          <ContagemDasFotos bandeja={bandeja} />
          <p className="mb-3 text-[0.8125rem] leading-snug text-texto-suave">
            Toque na foto para escolher. A lupa amplia sem escolher.
            {bandeja.temVersaoDaIa &&
              ` ${legendaDaVersao(bandeja.versao, LEGENDA_DA_PECA, LEGENDA_DA_FOTO)}`}
          </p>

          {bandeja.consulta.isLoading ? (
            <Girando className="mx-auto my-10 size-5 text-texto-fraco" />
          ) : bandeja.consulta.isError ? (
            <Falha
              mensagem={bandeja.consulta.error.message}
              tentarNovamente={() => void bandeja.consulta.refetch()}
            />
          ) : bandeja.itens.length === 0 ? (
            <p className="py-10 text-center text-[0.9375rem] text-texto-suave">
              {bandeja.filtrando
                ? "Nada com esses filtros. Tente outra grafia, ou limpe os filtros."
                : "Nenhuma foto publicada ainda. As fotos aprovadas e publicadas aparecem aqui."}
            </p>
          ) : (
            <>
              <GradeDeFotos
                fotos={bandeja.itens}
                versao={bandeja.versao}
                minimo={MINIMO_DA_GRADE.escolher}
                escolhida={escolhida}
                rotuloDoToque={(f) => rotuloDeEscolher(f.nome)}
                aoEscolher={(f) => aoEscolher(f.id)}
                aoAmpliar={(f) => setAmpliando(f.id)}
              />
              <MostrarMaisFotos bandeja={bandeja} />
            </>
          )}
        </div>
      </div>

      {emVisor && (
        <VisorDaFoto
          foto={emVisor}
          versao={bandeja.versao}
          acao={{
            rotulo: "Usar esta foto",
            aoAcionar: (versao) => {
              // A versão que estava à vista no visor passa a ser a da bandeja: é a que a geração usa.
              bandeja.escolherVersao(versao);
              aoEscolher(emVisor.id);
            },
          }}
          aoFechar={() => setAmpliando(null)}
        />
      )}
    </Sobreposicao>
  );
}
